import { Subproject, SubprojectStatus } from './types';

/**
 * Get overall status from stage statuses
 */
export function getOverallStatus(sub: Subproject): SubprojectStatus {
  const stage1 = sub.stage1Status || sub.status || 'not-started';
  const stage2 = sub.stage2Status || 'not-started';
  const stage3 = sub.stage3Status || 'not-started';

  // If all stages are finished, overall is finished
  if (stage1 === 'finished' && stage2 === 'finished' && stage3 === 'finished') {
    return 'finished';
  }
  // If any stage is in-progress or finished, overall is in-progress
  if (
    stage1 !== 'not-started' ||
    stage2 !== 'not-started' ||
    stage3 !== 'not-started'
  ) {
    return 'in-progress';
  }
  // Otherwise, not-started
  return 'not-started';
}

/**
 * Get primary status styling for subproject cards - matching project statuses
 */
export function getSubprojectStatusStyle(status?: string) {
  // Map subproject status to project-like status for display
  const displayStatus =
    status === 'finished'
      ? 'Completed'
      : status === 'in-progress'
        ? 'Active'
        : status === 'pencils-down'
          ? 'Pencils Down'
          : 'Active';

  switch (displayStatus) {
    case 'Active':
      return { bg: 'rgba(9, 126, 113, 1)', color: '#FFFFFF', label: 'Active' };
    case 'Completed':
      return {
        bg: 'rgba(29, 185, 151, 1)',
        color: '#FFFFFF',
        label: 'Completed',
      };
    case 'Pencils Down':
      return {
        bg: 'rgba(123, 188, 177, 1)',
        color: 'rgba(19, 54, 86, 1)',
        label: 'Pencils Down',
      };
    default:
      return { bg: 'rgba(9, 126, 113, 1)', color: '#FFFFFF', label: 'Active' };
  }
}

/**
 * Get progress label based on stage completion status
 */
export function getSubprojectProgress(sub: Subproject): { label: string } {
  const stage1 = sub.stage1Status || sub.status || 'not-started';
  const stage2 = sub.stage2Status || 'not-started';
  const stage3 = sub.stage3Status || 'not-started';

  // Stage 3 complete - all done
  if (stage3 === 'finished') {
    return { label: 'Energy Sources Complete' };
  }

  // Stage 3 in progress
  if (stage3 === 'in-progress') {
    return { label: 'Begin Energy Sources' };
  }

  // Stage 2 complete, stage 3 not started
  if (stage2 === 'finished') {
    return { label: 'SubSystems Complete' };
  }

  // Stage 2 in progress
  if (stage2 === 'in-progress') {
    return { label: 'Begin SubSystems' };
  }

  // Stage 1 complete, stage 2 not started
  if (stage1 === 'finished') {
    return { label: 'Space Usage Complete' };
  }

  // Stage 1 in progress
  if (stage1 === 'in-progress') {
    return { label: 'Space Usage In Progress' };
  }

  // Stage 1 not started
  return { label: 'Begin Space Usage' };
}

/**
 * Get sequential progress bar statuses - bars show cumulative progress
 * Returns what each of the 3 progress bars should display
 */
export function getSequentialBarStatuses(sub: Subproject): {
  bar1: SubprojectStatus;
  bar2: SubprojectStatus;
  bar3: SubprojectStatus;
} {
  const stage1 = sub.stage1Status || sub.status || 'not-started';
  const stage2 = sub.stage2Status || 'not-started';
  const stage3 = sub.stage3Status || 'not-started';

  // Bar 1 always shows stage 1 status
  const bar1 = stage1;

  // Bar 2 only shows progress if stage 1 is finished
  const bar2 = stage1 === 'finished' ? stage2 : 'not-started';

  // Bar 3 only shows progress if stages 1 and 2 are finished
  const bar3 =
    stage1 === 'finished' && stage2 === 'finished' ? stage3 : 'not-started';

  return { bar1, bar2, bar3 };
}
